/**
 * useSalesData(path, scope, extra) - GET a sales-hub endpoint for the current scope.
 *
 *   const hub = useSalesData('hub', scope, { dimension: 'sku' });
 *   const board = useSalesData('leaderboard', scope);
 *   const plats = useSalesData('platforms', scope);
 *   const detail = useSalesData(sku ? 'detail' : null, scope, { sku, search: null });
 *
 *   path   'hub' | 'detail' | 'leaderboard' | 'platforms' | 'returns' (see SALES_ENDPOINTS) or a full '/api/...' path.
 *          A falsy path means "idle": nothing is fetched and loading is false.
 *   scope  the `scope` object from useSalesScope (or any plain params object).
 *   extra  params merged over the scope; null / '' / 'ALL' removes a key (e.g. { search: null }).
 *
 * Returns { data, loading, error, notFound, refresh, url }. Every change of URL (or refresh())
 * aborts the previous request and stale responses are ignored. `data` keeps the last good
 * response while the next one loads, so pages can dim instead of blanking. `error` is the
 * axios error (pass it straight to <LoadError error={error} />).
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { getAuthHeaders } from '../../contexts/AuthContext';
import { scopeToParams } from './useSalesScope';

export const SALES_ENDPOINTS = {
  hub: '/api/sales-hub',
  detail: '/api/sales-hub/detail',
  leaderboard: '/api/sales-hub/leaderboard',
  platforms: '/api/sales-hub/platforms',
  returns: '/api/sales-hub/returns',
};

const isEmpty = value => value == null || value === '' || value === 'ALL';

/** Query string for a scope plus extra params (extra wins; empty/ALL values dropped). */
export const salesQuery = (scope, extra = {}) => {
  const merged = { ...scopeToParams(scope), ...extra };
  const params = new URLSearchParams();
  Object.keys(merged).sort().forEach(key => { if (!isEmpty(merged[key])) params.set(key, String(merged[key])); });
  return params.toString();
};

export const salesUrl = (path, scope, extra) => {
  const endpoint = SALES_ENDPOINTS[path] || path;
  const query = salesQuery(scope, extra);
  return `${process.env.REACT_APP_BACKEND_URL || ''}${endpoint}${query ? `?${query}` : ''}`;
};

export const useSalesData = (path, scope, extra = {}) => {
  const url = path ? salesUrl(path, scope, extra) : null;
  const [state, setState] = useState({ data: null, loading: Boolean(url), error: null });
  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => setVersion(value => value + 1), []);

  useEffect(() => {
    if (!url) { setState(prev => (prev.loading || prev.error ? { ...prev, loading: false, error: null } : prev)); return undefined; }
    const controller = new AbortController();
    setState(prev => ({ ...prev, loading: true, error: null }));
    axios.get(url, { headers: getAuthHeaders(), signal: controller.signal })
      .then(response => { if (!controller.signal.aborted) setState({ data: response.data, loading: false, error: null }); })
      .catch(reason => {
        if (controller.signal.aborted || axios.isCancel?.(reason)) return;
        setState(prev => ({ data: reason?.response?.status === 404 ? null : prev.data, loading: false, error: reason || new Error('Sales data could not be loaded.') }));
      });
    return () => controller.abort();
  }, [url, version]);

  return { ...state, notFound: state.error?.response?.status === 404, refresh, url };
};

export default useSalesData;
